import { type JSX } from 'react/jsx-runtime';
import { useCallback, useEffect } from 'react';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { LeftNavCtaMessage } from '../../../common/components/LeftNavCtaMessage';
import { JiraLeftNavCtaMessage } from '../../../common/components/JiraLeftNavCtaMessage';
import { DEFAULT_VARIANT, JiraSideNavVariant } from '../../../common/constants';
import { createRender, type HydrationResult } from './types';

/**
 * Main render function that selects the appropriate variant based on variantType from hydration.
 * control (null) → show nothing
 * existing → same banner as Confluence (LeftNavCtaMessage)
 * new → Jira-specific design (JiraLeftNavCtaMessage)
 *
 * Analytics (mount/dismiss/click) are fired here so both variants get the same tracking.
 */
export const render: (
	renderProps: Omit<HydrationResult, 'postOffice'> & {
		onDismissed: () => void;
	},
) => JSX.Element | null = createRender(({ variantType, onDismissed }) => {
	const variant = variantType ?? DEFAULT_VARIANT;
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

	// Fire viewed event on mount (for non-control variants)
	useEffect(() => {
		if (variant !== JiraSideNavVariant.CONTROL) {
			fireAnalyticsEvent(
				{
					eventType: 'ui',
					action: 'viewed',
					actionSubject: 'jiraRovoExtensionLeftNavCTA',
					actionSubjectId: 'post-office.rovo-extension-install-cta-left-nav.jira-side-nav',
				},
				{
					variant,
					source: 'jira-side-nav',
				},
			);
		}
	}, [fireAnalyticsEvent, variant]);

	const handleDismissWithAnalytics = useCallback(() => {
		fireAnalyticsEvent(
			{
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'jiraRovoExtensionLeftNavCTA',
				actionSubjectId: 'post-office.rovo-extension-install-cta-left-nav.dismiss',
			},
			{
				variant,
				source: 'jira-side-nav',
			},
		);
		onDismissed();
	}, [fireAnalyticsEvent, variant, onDismissed]);

	const handleLinkClickAnalytics = useCallback(() => {
		fireAnalyticsEvent(
			{
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'jiraRovoExtensionLeftNavCTA',
				actionSubjectId: 'post-office.rovo-extension-install-cta-left-nav.install',
			},
			{
				variant,
				source: 'jira-side-nav',
			},
		);
	}, [fireAnalyticsEvent, variant]);

	switch (variant) {
		case JiraSideNavVariant.EXISTING:
			return (
				<LeftNavCtaMessage
					context={{
						experimentCohort: 'copy-version-c',
						source: 'jira-side-nav',
					}}
					handleOnDismissed={handleDismissWithAnalytics}
					onSideNavClick={() => {
						handleLinkClickAnalytics();
						return null;
					}}
				/>
			);
		case JiraSideNavVariant.NEW:
			return (
				<JiraLeftNavCtaMessage
					context={{
						experimentCohort: 'jira-take-anywhere',
					}}
					handleOnDismissed={handleDismissWithAnalytics}
					onLinkClick={handleLinkClickAnalytics}
				/>
			);
		case JiraSideNavVariant.ROVO:
			return (
				<JiraLeftNavCtaMessage
					context={{
						experimentCohort: 'jira-rovo',
					}}
					handleOnDismissed={handleDismissWithAnalytics}
					onLinkClick={handleLinkClickAnalytics}
				/>
			);
		case JiraSideNavVariant.CONTROL:
		default:
			return null;
	}
});

export default render;
